/**
 * Support references for errors a user sees (FIX-LOG 93).
 *
 * Every failure shown to a user ends with "Reference LC-7K2Q9AB1". The
 * reference is the first eight characters of the Sentry event id, and every
 * event carries it as the `support_ref` tag, so `support_ref:LC-7K2Q9AB1` in
 * Sentry (project ledger-core) finds the event. The server mints the reference
 * for its own 5xx failures (body `support_ref`, header `X-Support-Ref`); this
 * module mints one for failures only the browser sees (network, render, a 5xx
 * from the load balancer). Expected 4xx outcomes are the user's to fix and are
 * never reported.
 *
 * Framework-free: main.tsx plugs Sentry in with `setErrorReporter`, so the
 * unit tests run without a browser or a Sentry client.
 */

export const SUPPORT_REF_HEADER = "X-Support-Ref";
const REF_PREFIX = "LC";

export interface ReportContext {
  /** Where the user saw it: "query", "mutation", "chat", "route_error", … */
  surface: string;
}

export interface FeedbackInput {
  message: string;
  url?: string;
  section?: string;
  ticketId?: string;
}

export interface ErrorReporter {
  /** Capture at error level; return the Sentry event id (or undefined). */
  capture(error: unknown, tags: Record<string, string>): string | undefined;
  /** Send User Feedback; return its id (or undefined). */
  feedback?(input: FeedbackInput & { associatedEventId?: string }, tags: Record<string, string>): string | undefined;
}

let reporter: ErrorReporter | null = null;
let latest: { ref: string; eventId?: string } | null = null;

export function setErrorReporter(r: ErrorReporter | null): void {
  reporter = r;
}

export function supportRefFromEventId(eventId: string): string {
  return `${REF_PREFIX}-${eventId.replace(/-/g, "").slice(0, 8).toUpperCase()}`;
}

function randomHex(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return Math.random().toString(16).slice(2).padEnd(8, "0");
}

export function rememberSupportRef(ref: string, eventId?: string): void {
  latest = { ref, eventId };
}

/** The reference the user saw most recently (for the Feedback button). */
export function latestSupportRef(): { ref: string; eventId?: string } | null {
  return latest;
}

type ErrorShape = {
  name?: string;
  status?: number;
  statusCode?: number;
  supportRef?: string;
  response?: { status?: number; headers?: { get?: (name: string) => string | null } };
  data?: { support_ref?: unknown; error?: { support_ref?: unknown } };
};

export function httpStatus(err: unknown): number | undefined {
  const e = err as ErrorShape | null;
  const s = e?.status ?? e?.statusCode ?? e?.response?.status;
  return typeof s === "number" && s > 0 ? s : undefined;
}

/** The reference the server attached to this failure, if it did. */
export function serverSupportRef(err: unknown): string | undefined {
  const e = err as ErrorShape | null;
  if (!e) return undefined;
  const candidates = [
    e.supportRef,
    e.data?.support_ref,
    e.data?.error?.support_ref,
    e.response?.headers?.get?.(SUPPORT_REF_HEADER) ?? undefined,
  ];
  for (const c of candidates) if (typeof c === "string" && c.trim()) return c.trim();
  return undefined;
}

/** A failure the user can act on (4xx) or chose (an aborted request). */
export function isExpectedFailure(err: unknown): boolean {
  const e = err as ErrorShape | null;
  if (e?.name === "AbortError") return true;
  const s = httpStatus(err);
  return s !== undefined && s >= 400 && s < 500;
}

/**
 * Report a failure the user is shown and return its reference, or undefined
 * for an expected 4xx / abort. A failure the server already reported keeps the
 * server's reference and is not captured twice.
 */
export function reportUserError(err: unknown, ctx: ReportContext): string | undefined {
  const fromServer = serverSupportRef(err);
  if (fromServer) {
    rememberSupportRef(fromServer);
    return fromServer;
  }
  if (isExpectedFailure(err)) return undefined;
  const tags: Record<string, string> = { user_error: ctx.surface };
  const status = httpStatus(err);
  if (status) tags.http_status = String(status);
  let eventId: string | undefined;
  try {
    eventId = reporter?.capture(err, tags);
  } catch {
    eventId = undefined;
  }
  const ref = supportRefFromEventId(eventId || randomHex());
  rememberSupportRef(ref, eventId);
  return ref;
}

/** `text` followed by "Reference LC-…." unless it already names it. */
export function appendSupportRef(text: string, ref: string | undefined): string {
  if (!ref || text.includes(ref)) return text;
  const base = text.trimEnd();
  const sep = /[.!?]$/.test(base) ? " " : ". ";
  return `${base}${sep}Reference ${ref}.`;
}

/** Report `err` and return `text` with its reference appended. */
export function withSupportRef(text: string, err: unknown, ctx: ReportContext): string {
  return appendSupportRef(text, reportUserError(err, ctx));
}

/**
 * The Error a chat stream throws for a non-2xx response, carrying the status
 * and the server's reference so `withSupportRef` neither re-reports it nor
 * reports an expected 4xx.
 */
export async function streamHttpError(response: {
  status: number;
  statusText: string;
  headers?: { get(name: string): string | null };
  json(): Promise<unknown>;
}): Promise<Error> {
  let text = `${response.status} ${response.statusText}`;
  let ref = response.headers?.get(SUPPORT_REF_HEADER) ?? undefined;
  try {
    const j = (await response.json()) as { detail?: unknown; support_ref?: unknown };
    if (typeof j?.detail === "string" && j.detail) text = j.detail;
    if (typeof j?.support_ref === "string" && j.support_ref) ref = j.support_ref;
  } catch {
    /* body wasn't JSON — keep the status text */
  }
  return Object.assign(new Error(text), { status: response.status, supportRef: ref || undefined });
}

/** The bubble text after a chat `error` event: what was said, then the failure. */
export function appendStreamError(sofar: string | undefined, errorLine: string): string {
  return sofar && sofar.trim() ? `${sofar}\n\n${errorLine}` : errorLine;
}

/** Note a reference the server put on a chat `error` event. */
export function rememberStreamErrorRef(data: Record<string, unknown> | null | undefined): void {
  const ref = data?.support_ref;
  if (typeof ref === "string" && ref) rememberSupportRef(ref);
}

/**
 * Send what the user said through the Feedback button to Sentry User
 * Feedback, with the page, section and the latest reference they saw.
 */
export function sendFeedback(input: FeedbackInput): string | undefined {
  if (!reporter?.feedback) return undefined;
  const last = latestSupportRef();
  const tags: Record<string, string> = { feedback_source: "feedback_button" };
  if (input.section) tags.section = input.section;
  if (input.ticketId) tags.feedback_ticket = input.ticketId;
  if (last) tags.latest_support_ref = last.ref;
  const message = input.message.trim() || "(Feedback sent with the Feedback button; no narration.)";
  try {
    return reporter.feedback({ ...input, message, associatedEventId: last?.eventId }, tags);
  } catch {
    return undefined;
  }
}

/** Entity and user id from the session token (no other claims, no PII). */
export function tokenIdentity(token: string | null): { userId?: string; entityId?: string } {
  if (!token) return {};
  try {
    const part = token.split(".")[1];
    if (!part) return {};
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { sub?: unknown; entity_id?: unknown };
    return {
      userId: typeof claims.sub === "string" ? claims.sub : undefined,
      entityId: typeof claims.entity_id === "string" ? claims.entity_id : undefined,
    };
  } catch {
    return {};
  }
}
